import React, { useEffect, useState } from 'react'
import { useApi, useOrder, useUtils, useLanguage, useConfig } from '~components'
import Skeleton from 'react-loading-skeleton'
import { useTheme } from 'styled-components'
import {
  generalUtilities
} from '~ui'
import {
  BusinessDetail
} from './styles'

const LocationSelect = ({ storeId, selectedIdx, handelBusinessClick }) => {
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [businessData, setBusinessData] = useState([]) // for first API
  const [ordering] = useApi()
  const [orderState] = useOrder()
  const [{ parsePrice, parseDistance }] = useUtils()
  const { convertHoursToMinutes } = generalUtilities()
  const theme = useTheme()
  const [, t] = useLanguage()
  const [{ configs }] = useConfig()
  const hideDeliveryFee = theme?.business_view?.components?.header?.components?.business?.components?.fee?.hidden
  const hideTime = theme?.business_view?.components?.header?.components?.business?.components?.time?.hidden
  const hideDistance = theme?.business_view?.components?.header?.components?.business?.components?.distance?.hidden
  const unaddressedTypes = configs?.unaddressed_order_types_allowed?.value.split('|').map(value => Number(value)) || []
  const isAllowUnaddressOrderType = unaddressedTypes.includes(orderState?.options?.type)
  useEffect(() => {
    const fetchBusinessesByCoordinates = async () => {
      setLoading(true)
      try {
        const defaultLatitude = Number(configs?.location_default_latitude?.value)
        const defaultLongitude = Number(configs?.location_default_longitude?.value)
        const isInvalidDefaultLocation = isNaN(defaultLatitude) || isNaN(defaultLongitude)
        const defaultLocation = {
          lat: !isInvalidDefaultLocation ? defaultLatitude : 40.7744146,
          lng: !isInvalidDefaultLocation ? defaultLongitude : -73.9678064
        }
        const parameters = {
          location: isAllowUnaddressOrderType && !orderState.options?.address?.location
            ? defaultLocation
            : `${orderState.options?.address?.location?.lat},${orderState.options?.address?.location?.lng}`,
          type: orderState.options?.type || 1,
          page: 1,
          page_size: 25,
          params: 'id,name,slug,distance,delivery_time,pickup_time,location,address,delivery_price'
        }
        const response = await ordering.businesses()
          .select(['id', 'name', 'slug', 'distance', 'delivery_time', 'pickup_time', 'address', 'delivery_price'])
          .parameters(parameters)
          .get()
        if (!response.content.error) {
          setBusinessData(response.content.result || [])
        } else {
          console.log(response.content.result.error || 'Error fetching businesses')
          setBusinessData([])
        }
      } catch (err) {
        console.log(err.message || 'Unexpected error')
        setBusinessData([])
      } finally {
        setLoading(false)
      }
    }
    fetchBusinessesByCoordinates()
  }, [
    ordering,
    configs,
    isAllowUnaddressOrderType,
    orderState.options?.address?.location,
    orderState.options?.type
  ])

  // Fetch group stores (branches) for pickup
  const getGroups = async (id) => {
    setLoading(true)
    try {
      const data = await fetch(`https://plugins-development.ordering.co/giftme/business-branch/get_branch.php?store_id=${id}`)
      const response = await data.json()
      // Merge with businessData for additional info
      const merged = (response?.stores || []).map(store => {
        const matched = businessData.find(b => b.id === store.store_id)
        if (matched) {
          return {
            ...store,
            ...matched,
            distance: matched.distance || store.distance,
            delivery_time: matched.delivery_time || store.delivery_time,
            pickup_time: matched.pickup_time || store.pickup_time,
            address: matched.address || store.address,
            delivery_price: matched.delivery_price || store.delivery_price,
            slug: matched.slug || store.slug
          }
        }
        return null
      }).filter(Boolean)

      setLocations(merged)
    } catch (error) {
      console.error('Error fetching locations:', error)
      setLocations([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (storeId && businessData.length > 0) {
      getGroups(storeId)
    }
  }, [storeId, businessData])

  const onSelect = (slug, index) => {
    if (index !== selectedIdx) {
      handelBusinessClick(slug)
    }
  }

  if (loading) return <div>Loading...</div>
  if (!locations.length) return <div>No locations available</div>

  return (
    <div style={{ background: '#fff', width: '95%' }}>
      {locations.map((business, i) => (
        <label
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: i < locations.length - 1 ? '1px solid #eee' : 'none',
            cursor: 'pointer'
          }}
        >
          <input
            type='radio'
            checked={storeId === business.id}
            onChange={() => onSelect(business?.slug, i)}
            style={{ marginRight: 16 }}
          />
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              <span style={{
                fontWeight: 500,
                display: 'inline-block',
                maxWidth: 300, // adjust width as needed
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                verticalAlign: 'middle'
              }}>
                {business.address}
              </span>{' '}
            </div>
            <BusinessDetail>
              {orderState?.options.type === 1 && !hideDeliveryFee && (
                <>
                  {!loading
                    ? (
                      <>
                        <h5>
                          <span>{t('DELIVERY_FEE', 'Delivery fee')}</span>
                          {business && parsePrice(business?.delivery_price || 0)}
                        </h5>
                        <span className='dot'>•</span>
                      </>
                      )
                    : (
                      <Skeleton width={50} />
                      )}
                </>
              )}

              {!hideTime && (
                <>
                  {!loading
                    ? (
                      <>

                        {orderState?.options?.type === 1
                          ? (
                            <>
                              <h5>
                                {convertHoursToMinutes(business?.delivery_time)}
                              </h5>
                              <span className='dot'>•</span>
                            </>
                            )
                          : (
                            <>
                              <h5>
                                {convertHoursToMinutes(business?.pickup_time)}
                              </h5>
                              <span className='dot'>•</span>
                            </>
                            )}
                      </>
                      )
                    : (
                      <Skeleton width={50} />
                      )}
                </>
              )}
              {!hideDistance && (
                <>
                  {!loading
                    ? (
                      <>
                        <h5>
                          {parseDistance(business?.distance || 0)}
                        </h5>

                      </>
                      )
                    : (
                      <Skeleton width={50} />
                      )}
                </>
              )}

            </BusinessDetail>
          </div>
        </label>
      ))}
    </div>
  )
}

export default LocationSelect
